/**
 * PlanFeatureBanner — explains API capability limits before the user reaches
 * the backend 402 gate. The parent page supplies the already-resolved access
 * level from useApiAccessLevel so profile entitlements have one source of truth
 * and are not fetched twice.
 *
 * `none` blocks read/write access; `read` permits list views but blocks all
 * mutations; `full` renders no banner. Copy describes the required capability
 * rather than guessing a tenant's current marketing tier.
 */
import React from 'react';
import { Alert, Button } from '@mui/material';
import RocketLaunchOutlined from '@mui/icons-material/RocketLaunchOutlined';
import { useTranslation } from 'react-i18next';

import type { ApiAccessLevel } from '../../hooks/useApiAccessLevel';

export const PlanFeatureBanner: React.FC<{
  level: ApiAccessLevel;
  loaded: boolean;
  requires?: 'read' | 'write';
}> = ({ level, loaded, requires = 'write' }) => {
  const { t } = useTranslation();

  if (!loaded || level === 'full') return null;

  const handleUpgrade = () => {
    window.dispatchEvent(new CustomEvent('svdy:upgrade:open'));
  };

  const upgradeAction = (
    <Button
      color="inherit"
      size="small"
      onClick={handleUpgrade}
      sx={{ textTransform: 'none', fontWeight: 600 }}
    >
      {t('integrations:planGate.upgradePlan')}
    </Button>
  );

  if (level === 'none') {
    return (
      <Alert
        severity="error"
        icon={<RocketLaunchOutlined fontSize="small" />}
        sx={{ width: '100%', mb: 2, borderRadius: 1 }}
        action={upgradeAction}
      >
        <strong>{t('integrations:planGate.noneTitle')}</strong>{' '}
        {t('integrations:planGate.noneBody')}
      </Alert>
    );
  }

  if (requires === 'write') {
    return (
      <Alert
        severity="warning"
        sx={{ width: '100%', mb: 2, borderRadius: 1 }}
        action={upgradeAction}
      >
        <strong>{t('integrations:planGate.readTitle')}</strong>{' '}
        {t('integrations:planGate.readBody')}
      </Alert>
    );
  }

  return null;
};

export default PlanFeatureBanner;
